"use client";

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { Badge, Button } from "skedyul-ui/ui";
import { trpc } from "@utils/api";
import type { RootState } from "@store/index";
import { store } from "@store/index";
import { close as closeSideNavigation } from "@store/features/layout/sideNavigationSlice";
import {
  consumePromoteScenariosCallback,
  type PromoteScenariosSidebarParams,
} from "@/lib/openPromoteScenariosSidebar";

type ScenarioRow = {
  id: string;
  name: string;
  description: string | null;
};

type RunStatus = "queued" | "running" | "passed" | "failed";

type ScenarioRun = ScenarioRow & {
  status: RunStatus;
  failures: string[];
};

export const PromoteScenariosSidebarHost: React.FC = () => {
  const params = useSelector(
    (state: RootState) =>
      state.sideNavigation.right.params as PromoteScenariosSidebarParams,
  );
  const isOpen = useSelector(
    (state: RootState) =>
      state.sideNavigation.right.isOpen &&
      state.sideNavigation.right.schema === "PROMOTE_SCENARIOS",
  );

  const [runs, setRuns] = useState<ScenarioRun[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  const close = useCallback(() => {
    store.dispatch(closeSideNavigation({ position: "right" }));
  }, []);

  useEffect(() => {
    if (!isOpen || !params?.id || !params.versionId) {
      setRuns([]);
      setHasStarted(false);
      setIsRunning(false);
      setIsPromoting(false);
      return;
    }

    let cancelled = false;
    const runAll = async () => {
      setIsLoading(true);
      try {
        const listed =
          params.kind === "skill"
            ? await trpc.skills.listScenarios.query({ skillId: params.id })
            : await trpc.agents.listScenarios.query({ agentId: params.id });
        if (cancelled) return;
        const nextRuns: ScenarioRun[] = listed.map((scenario) => ({
          id: scenario.id,
          name: scenario.name,
          description: scenario.description,
          status: "queued",
          failures: [],
        }));
        setRuns(nextRuns);
        setHasStarted(true);
        if (nextRuns.length === 0) {
          setIsLoading(false);
          return;
        }

        setIsRunning(true);
        for (const scenario of nextRuns) {
          if (cancelled) return;
          setRuns((prev) =>
            prev.map((row) =>
              row.id === scenario.id ? { ...row, status: "running" } : row,
            ),
          );
          try {
            const result =
              params.kind === "skill"
                ? await trpc.skills.runScenario.mutate({
                    scenarioId: scenario.id,
                    skillVersionId: params.versionId,
                  })
                : await trpc.agents.runScenario.mutate({
                    scenarioId: scenario.id,
                    agentVersionId: params.versionId,
                  });
            if (cancelled) return;
            setRuns((prev) =>
              prev.map((row) =>
                row.id === scenario.id
                  ? {
                      ...row,
                      status: result.passed ? "passed" : "failed",
                      failures: result.failures,
                    }
                  : row,
              ),
            );
          } catch (error) {
            if (cancelled) return;
            setRuns((prev) =>
              prev.map((row) =>
                row.id === scenario.id
                  ? {
                      ...row,
                      status: "failed",
                      failures: [
                        error instanceof Error
                          ? error.message
                          : "Failed to run scenario",
                      ],
                    }
                  : row,
              ),
            );
          }
        }
      } catch {
        if (!cancelled) {
          toast.error("Failed to load scenarios");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsRunning(false);
        }
      }
    };

    void runAll();
    return () => {
      cancelled = true;
    };
  }, [isOpen, params?.kind, params?.id, params?.versionId]);

  const allPassed =
    hasStarted &&
    !isLoading &&
    !isRunning &&
    runs.length > 0 &&
    runs.every((run) => run.status === "passed");
  const isEmpty = hasStarted && !isLoading && runs.length === 0;
  const canPromote = allPassed || isEmpty;

  const handlePromote = async (confirmEmptyScenarios: boolean) => {
    if (!params?.id || !params.versionId) return;
    setIsPromoting(true);
    try {
      if (params.kind === "skill") {
        await trpc.skills.deployVersion.mutate({
          skillId: params.id,
          versionId: params.versionId,
          confirmEmptyScenarios,
        });
      } else {
        await trpc.agents.publishMarketplaceVersion.mutate({
          agentId: params.id,
          versionId: params.versionId,
          trafficWeight: 100,
          confirmEmptyScenarios,
        });
      }
      toast.success("Live version updated");
      await consumePromoteScenariosCallback()?.();
      close();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to promote version",
      );
    } finally {
      setIsPromoting(false);
    }
  };

  if (!params?.id || !params.versionId) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
        Promote details unavailable.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b px-6 py-4">
        <div className="text-base font-semibold">Run scenarios</div>
        <p className="mt-1 text-sm text-muted-foreground">
          {params.versionLabel} must pass every scenario before it can go live.
        </p>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto px-6 py-4">
        {isLoading && !hasStarted ? (
          <p className="text-sm text-muted-foreground">Loading scenarios…</p>
        ) : isEmpty ? (
          <div className="rounded-lg border p-4">
            <div className="text-sm font-medium">No scenarios yet</div>
            <p className="mt-1 text-sm text-muted-foreground">
              This version can go live without tests. Create scenarios to gate
              the next promotion.
            </p>
          </div>
        ) : (
          runs.map((run) => (
            <div key={run.id} className="rounded-lg border p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">{run.name}</div>
                  {run.description ? (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {run.description}
                    </p>
                  ) : null}
                </div>
                <Badge
                  variant={
                    run.status === "passed"
                      ? "default"
                      : run.status === "failed"
                        ? "destructive"
                        : "secondary"
                  }
                  label={
                    run.status === "queued"
                      ? "Queued"
                      : run.status === "running"
                        ? "Running"
                        : run.status === "passed"
                          ? "Passed"
                          : "Failed"
                  }
                />
              </div>
              {run.failures.length > 0 ? (
                <p className="mt-2 text-xs text-destructive">
                  {run.failures.join(", ")}
                </p>
              ) : null}
            </div>
          ))
        )}
      </div>
      <div className="flex justify-end gap-2 border-t px-6 py-4">
        <Button
          label="Cancel"
          variant="outline"
          size="sm"
          handleClick={close}
        />
        {isEmpty ? (
          <Button
            label="Go live with no scenarios"
            size="sm"
            isDisabled={isPromoting}
            isLoading={isPromoting}
            handleClick={() => void handlePromote(true)}
          />
        ) : (
          <Button
            label="Promote to Live"
            size="sm"
            isDisabled={!canPromote || isPromoting}
            isLoading={isPromoting}
            handleClick={() => void handlePromote(false)}
          />
        )}
      </div>
    </div>
  );
};
